import { CommonModule, Location } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { PatientProvider } from '../../../care-team/models/patient-provider';
import { getPatientCareTeam } from '../../../care-team/store/actions/care-team.actions';
import { selectCareTeamMemberById, selectIsLoading } from '../../../care-team/store/selectors/care-team.selector';
import { loadPageWithPatientIdParameter } from '../../../core/store/actions/app.actions';
import { selectIsMobileBrowser } from '../../../core/store/selectors/app.selector';

import { IconName } from '../../../shared/components/icon/icon-data.generated';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { PageHeaderComponent } from '../../../shared/components/page-header/page-header.component';
import { ProgressSpinnerComponent } from '../../../shared/components/progress-spinner/progress-spinner.component';
import { topics } from '../../data/topics';

type TopicVm = {
  messageTopicId: number;
  topic: string;
  description: string;
  iconName: IconName;
  accent: string; // hex used for left accent + icon bubble
};

@Component({
  selector: 'app-topic-selection',
  imports: [CommonModule, LetDirective, ProgressSpinnerComponent, IconComponent, PageHeaderComponent],
  templateUrl: './topic-selection.component.html',
  styleUrl: './topic-selection.component.scss',
})
export class TopicSelectionComponent implements OnInit {
  private store = inject(Store);
  private location = inject(Location);
  @Input() isModal: boolean = false;

  providerId!: string;

  @Input() set providerUserId(value: string) {
    this.providerId = value;
    this.patientCareTeamMember$ = this.store.select(selectCareTeamMemberById(this.providerId));
  }

  @Output() messageTopicIdSelected = new EventEmitter<number>();

  patientCareTeamMember$!: Observable<PatientProvider | null>;
  isLoading$ = this.store.select(selectIsLoading);
  isMobileBrowser = this.store.selectSignal(selectIsMobileBrowser);

  // Build a view-model list with icons + accents (no changes needed to topics data file)
  topics: TopicVm[] = topics.map(t => ({
    messageTopicId: t.messageTopicId,
    topic: t.topic,
    description: t.description,
    iconName: this.getIconName(t.messageTopicId),
    accent: this.getAccentColor(t.messageTopicId),
  }));

  ngOnInit(): void {
    this.store.dispatch(getPatientCareTeam());
  }

  onRowKeydown(event: KeyboardEvent, messageTopicId: number) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onClick(messageTopicId);
    }
  }

  onClick(messageTopicId: number) {
    if (this.isMobileBrowser()) {
      this.store.dispatch(
        loadPageWithPatientIdParameter({ route: `messages/${this.providerId}/${messageTopicId}/new-message` })
      );
    } else {
      this.messageTopicIdSelected.emit(messageTopicId);
    }
  }

  onBack() {
    this.location.back();
  }

  private getIconName(messageTopicId: number): IconName {
    // Map IDs to Material icon names you already use via <app-icon>.
    // If your IDs differ, just tweak these cases.
    switch (messageTopicId) {
      case 1:
        return 'chat_bubble'; // non-urgent medical question
      case 2:
        return 'medication'; // medication question/refill
      case 3:
        return 'event'; // appointment
      case 4:
        return 'folder'; // records
      default:
        return 'chat_bubble';
    }
  }

  private getAccentColor(messageTopicId: number): string {
    // Soft, readable accents (work in light + dark). Adjust to your brand later if desired.
    switch (messageTopicId) {
      case 1:
        return '#4896E0'; // blue
      case 2:
        return '#8B7CFF'; // alt blue
      case 3:
        return '#4FAE7B'; // green
      case 4:
        return '#D4A64A'; // amber
      default:
        return '#4896E0';
    }
  }
}
